/**
 * client/src/pages/Characters.tsx — `/b/:bookId/characters`. Who is in this book, and who reads them.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * KARTI'S WORDS: *"for each book we need like a setting where we can see the characters and change
 * them and rerender the book."*
 *
 * Four things, in the order somebody actually uses them:
 *
 *   1. **the narrator**, pinned — everything not assigned to anybody is read by this voice;
 *   2. **the cast**, biggest speaker first, each with three real lines to read and a voice to pick;
 *   3. **the lines nobody is named in**, reported honestly and one press to assign;
 *   4. **find the characters**, which is priced before it runs and never touches a correction.
 *
 * ⚠️ **Nothing on this screen queues a render.** Choosing a voice re-plans the pages; the priced
 * buttons that already exist (a chapter, the whole book, pressing play) are what make them.
 * AGENTS.md's throttle: *"no code path may raise a book above one-page granularity on its own
 * initiative"*, and "I picked a voice for Holmes" is not consent to spend an hour of a shared GPU.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **Sibling of `/b/:bookId/structure`, and gated the same way.** `<Gate>` in `routes.tsx` is a
 * routing courtesy; the server refuses 404 through `openBookForEdit` (LAW 17), so a stranger's book
 * is not merely hidden here, it is unreachable.
 *
 * ⚠️ **Mobile first, at ~390 px.** The cast is a single column of cards that already stack, the
 * sample lines wrap, and every control is a full-width tap target — because the person most likely to
 * fix a wrong voice is the person who just heard it, in bed, on a phone.
 *
 * ⚠️ **No new CSS.** Every class here already exists in `client/src/ui/app.css`.
 */

import { useCallback, useEffect, useState, type ReactElement } from "react";
import { Link, useParams } from "react-router-dom";

import { AppFrame, Blank, IconBack } from "../App.tsx";
import { CharacterCard } from "../characters/CharacterCard.tsx";
import { Unattributed } from "../characters/Unattributed.tsx";
import type { CastScreen, DetectAnswer } from "../characters/wire.ts";
import { ApiError, api } from "../lib/api.ts";

export function Characters(): ReactElement {
  const { bookId = "" } = useParams();
  const [screen, setScreen] = useState<CastScreen | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detect, setDetect] = useState<DetectAnswer | null>(null);
  const [busy, setBusy] = useState(false);
  const [merging, setMerging] = useState<{ from: string; into: string }>({ from: "", into: "" });

  const base = `/books/${encodeURIComponent(bookId)}/characters`;

  const load = useCallback(async () => {
    try {
      setScreen(await api.get<CastScreen>(base));
      setError(null);
    } catch (err) {
      // A 404 is LAW 17's refusal — not your book, and it must not be distinguishable from "no such
      // book". One message for both, which is the whole point of refusing 404.
      setError(err instanceof ApiError ? err.message : "Couldn't open this book's characters.");
      setScreen(null);
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  const priceDetect = useCallback(async () => {
    setBusy(true);
    try {
      setDetect(await api.get<DetectAnswer>(`${base}/detect`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't work out what that would cost.");
    } finally {
      setBusy(false);
    }
  }, [base]);

  const runDetect = useCallback(async () => {
    setBusy(true);
    try {
      const got = await api.post<DetectAnswer & CastScreen>(`${base}/detect`, { apply: true });
      setDetect(got);
      setScreen(got);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't find the characters.");
    } finally {
      setBusy(false);
    }
  }, [base]);

  const merge = useCallback(
    async (apply: boolean) => {
      if (merging.from === "" || merging.into === "" || merging.from === merging.into) return;
      setBusy(true);
      try {
        const got = await api.post<{ applied: boolean; moving?: number; moved?: number } & Partial<CastScreen>>(`${base}/merge`, {
          ...merging,
          apply,
        });
        if (apply && got.characters !== undefined) {
          setScreen(got as CastScreen);
          setMerging({ from: "", into: "" });
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Couldn't merge those two.");
      } finally {
        setBusy(false);
      }
    },
    [base, merging],
  );

  if (error !== null) {
    return (
      <AppFrame>
        <section className="ab-refusal">
          <h1 className="ab-refusal-title">Not here</h1>
          <p className="ab-lede">{error}</p>
          <Link to="/" className="ab-btn ab-btn-ghost">
            Back to the shelf
          </Link>
        </section>
      </AppFrame>
    );
  }
  if (screen === null) return <Blank />;

  return (
    <AppFrame wide>
      <div className="ab-header-left">
        <Link to={`/b/${encodeURIComponent(bookId)}`} className="ab-back">
          <IconBack /> Back to the book
        </Link>
      </div>

      <h1 className="ab-h1">Who reads this book</h1>

      {/* ── 1. the narrator, pinned ─────────────────────────────────────── */}
      <section className="ab-panel">
        <h2 className="ab-h2">The narrator</h2>
        <p className="ab-lede">
          <strong>{screen.narrator.label}</strong> reads everything that is not assigned to somebody below — all the prose, and
          every line of dialogue nobody has been given.
        </p>
        <p className="ab-hint ab-hint-quiet">
          Changing the narrator is on the book's own screen, because it re-records the whole book rather than one person's lines.
        </p>
      </section>

      {/* ── 2. the cast ─────────────────────────────────────────────────── */}
      {screen.characters.length === 0 ? (
        <section className="ab-panel">
          <h2 className="ab-h2">Nobody yet</h2>
          <p className="ab-lede">
            This book has no cast. <em>Find the characters</em> below reads every line of dialogue in it and works out who is
            speaking wherever the book says so out loud — <em>“…,” said Holmes</em>.
          </p>
          <p className="ab-hint ab-hint-quiet">
            It costs no GPU and it changes no audio: everybody it finds is read by the narrator until you give them a voice.
          </p>
        </section>
      ) : (
        <section className="ab-panel">
          <h2 className="ab-h2">
            The cast <span className="ab-chip ab-chip-quiet">{screen.characters.length}</span>
          </h2>
          {/* The threshold, stated. Without it, "this line is Holmes's and it is read by the narrator"
              looks like a bug rather than the rule it is. */}
          <p className="ab-hint ab-hint-quiet">
            A line goes to a character when at least {(screen.speakerQuotedMin * 100).toFixed(0)}% of it is inside quotation
            marks. A short quote with a long tag — <em>“Run! Now!” she cried, and turned away</em> — stays with the narrator.
          </p>
          <div className="ab-grid">
            {screen.characters.map((c) => (
              <CharacterCard
                key={c.id}
                bookId={bookId}
                character={c}
                voices={screen.voices}
                narratorLabel={screen.narrator.label}
                onChanged={setScreen}
              />
            ))}
          </div>

          {screen.unavailable.length > 0 && (
            // Named, with the reason. A picker that silently has one fewer option is how somebody
            // restarts a machine that was never the problem.
            <ul className="ab-warnings">
              {screen.unavailable.map((v) => (
                <li key={v.id} className="ab-hint ab-hint-quiet">
                  <strong>{v.label}</strong> cannot be used: {v.reason ?? "no reason recorded"}
                </li>
              ))}
            </ul>
          )}

          {screen.characters.length > 1 && (
            <div className="ab-confirm-row">
              <h3 className="ab-h3">Two rows, one person</h3>
              <p className="ab-hint ab-hint-quiet">
                <em>Holmes</em> and <em>Sherlock Holmes</em> are usually the same man. Merging moves every line across and keeps
                both names; it costs nothing unless the two are read by different voices.
              </p>
              <div className="ab-fields">
                <select
                  className="ab-select"
                  value={merging.from}
                  onChange={(e) => setMerging((m) => ({ ...m, from: e.target.value }))}
                >
                  <option value="">merge…</option>
                  {screen.characters.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  className="ab-select"
                  value={merging.into}
                  onChange={(e) => setMerging((m) => ({ ...m, into: e.target.value }))}
                >
                  <option value="">…into</option>
                  {screen.characters.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="ab-btn ab-btn-ghost"
                  disabled={busy || merging.from === "" || merging.into === "" || merging.from === merging.into}
                  onClick={() => void merge(true)}
                >
                  Merge
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── 3. the honest report ────────────────────────────────────────── */}
      <Unattributed bookId={bookId} screen={screen} onChanged={setScreen} />

      {/* ── 4. find them ────────────────────────────────────────────────── */}
      <section className="ab-panel">
        <h2 className="ab-h2">Find the characters</h2>
        <p className="ab-hint ab-hint-quiet">
          Reads the book and writes down who speaks where the text says so. It never overwrites a line you assigned yourself.
        </p>

        {detect !== null && (
          <>
            <p className="ab-note">{detect.price.summary}</p>
            {/* The stage that is BUILT and not wired, named with its number rather than left as an
                absence. AGENTS.md's `<Soon>` rule: a stub that names its phase beats a screen that
                looks finished. */}
            {!detect.llm.available && <p className="ab-hint">{detect.llm.reason}</p>}
            {detect.written !== undefined && (
              <p className="ab-note">
                {detect.written.characters} characters · {detect.written.speakers.toLocaleString("en-GB")} lines attributed
                {detect.written.keptHuman > 0 && <> · your {detect.written.keptHuman.toLocaleString("en-GB")} corrections were kept</>}
              </p>
            )}
          </>
        )}

        <div className="ab-actions">
          <button type="button" className="ab-btn ab-btn-ghost" disabled={busy} onClick={() => void priceDetect()}>
            What would that cost?
          </button>
          <button type="button" className="ab-btn ab-btn-primary" disabled={busy} onClick={() => void runDetect()}>
            {screen.characters.length === 0 ? "Find the characters" : "Find them again"}
          </button>
        </div>
      </section>
    </AppFrame>
  );
}
